import {
  CommentEditorActionTypes,
  ISaveCommentSuccessAction,
} from '@app/components/CommentEditor/modules/commentEditor'
import {
  FeedItemEditorActionTypes,
  ICreateFeedItemSuccessAction,
} from '@app/components/FeedItemEditor/modules/feedItemEditor'
import { actionCreators as fileUploadActions } from '@app/components/FileUpload/modules/fileUpload'
import { Comment, IComment } from '@app/models/Comment'
import { CommentEditState } from '@app/models/CommentEditState'
import { FeedItemType } from '@app/models/enums'
import { FeedItem, IFeedItem } from '@app/models/FeedItem'
import { FeedItemEditState } from '@app/models/FeedItemEditState'
import { IShareContact, ShareContact } from '@app/models/ShareContact'
import { IShareFeedItem, ShareFeedItem } from '@app/models/ShareFeedItem'
import { ApplicationState } from '@app/store'
import { convertToMarkup } from '@app/utils/mention'
import { errorToast, successToast } from '@app/utils/notifications'
import {
  IRequestOptions,
  makeAuthenticatedRequest,
  RequestMethod,
} from '@app/utils/request'
import { EditorState } from 'draft-js'
import { List, Map, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeEvery, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------
export enum FeedItemScope {
  All = 'All',
  Mine = 'Mine',
}

export interface IShareFeedItemState {
  readonly shareItem?: IShareFeedItem
}

export interface IFeedItemsState {
  readonly error?: string
  readonly isLoading: boolean
  readonly items: Map<string, IFeedItem>
  readonly shareItems: Map<string, IShareFeedItem>
}

const FeedItemsStateRecord = Record({
  error: undefined,
  isLoading: false,
  items: Map<string, FeedItem>(),
  shareItems: Map<string, ShareFeedItem>(),
})

export class FeedItemsState
  extends FeedItemsStateRecord
  implements IFeedItemsState
{
  public static fromJS(initialState?: Partial<IFeedItemsState>) {
    if (!initialState) {
      return new FeedItemsState(initialState)
    }

    const { items, shareItems, ...rest } = initialState
    return new FeedItemsState({
      items: items
        ? Map<string, FeedItem>(items.map((i) => FeedItem.fromJS(i)))
        : Map<string, FeedItem>(),
      shareItems: shareItems
        ? Map<string, ShareFeedItem>(
            shareItems.map((i) => ShareFeedItem.fromJS(i)),
          )
        : Map<string, ShareFeedItem>(),
      ...rest,
    })
  }

  public readonly error?: string
  public readonly isLoading: boolean
  public readonly items: Map<string, FeedItem>
  public readonly shareItems: Map<string, ShareFeedItem>

  constructor(props?: Partial<FeedItemsState>) {
    props ? super(props) : super()
  }

  public getFeedItem(feedItemId: string): FeedItem | undefined {
    return this.items.get(feedItemId)
  }

  public getComment(
    feedItemId: string,
    commentId: string,
  ): Comment | undefined {
    const feedItem = this.getFeedItem(feedItemId)
    return feedItem && feedItem.comments
      ? feedItem.comments.find((c) => c !== undefined && c.id === commentId)
      : undefined
  }

  public getCommentIndex(feedItemId: string, commentId: string): number {
    const feedItem = this.getFeedItem(feedItemId)
    return feedItem && feedItem.comments
      ? feedItem.comments.findIndex(
          (c) => c !== undefined && c.id === commentId,
        )
      : -1
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ActionTypes {
  ArchiveLead = 'FEED_ITEMS::ARCHIVE_LEAD',
  ArchiveLeadFailure = 'FEED_ITEMS::ARCHIVE_LEAD_FAILURE',
  ArchiveLeadSuccess = 'FEED_ITEMS::ARCHIVE_LEAD_SUCCESS',
  BeginEdit = 'FEED_ITEMS::BEGIN_EDIT',
  BeginEditComment = 'FEED_ITEMS::BEGIN_EDIT_COMMENT',
  CancelEdit = 'FEED_ITEMS::CANCEL_EDIT',
  CancelEditComment = 'FEED_ITEMS::CANCEL_EDIT_COMMENT',
  CancelShareFeedItemModal = 'FEED_ITEMS::CANCEL_SHARE_FEED_ITEM_MODAL',
  ClearFeedItems = 'FEED_ITEMS::CLEAR_FEED_ITEMS',
  DeleteComment = 'FEED_ITEMS::DELETE_COMMENT',
  DeleteCommentFailure = 'FEED_ITEMS::DELETE_COMMENT_FAILURE',
  DeleteCommentRequest = 'FEED_ITEMS::DELETE_COMMENT_REQUEST',
  DeleteCommentSuccess = 'FEED_ITEMS::DELETE_COMMENT_SUCCESS',
  DeleteFeedItem = 'FEED_ITEMS::DELETE_FEED_ITEM',
  DeleteFeedItemFailure = 'FEED_ITEMS::DELETE_FEED_ITEM_FAILURE',
  DeleteFeedItemRequest = 'FEED_ITEMS::DELETE_FEED_ITEM_REQUEST',
  DeleteFeedItemSuccess = 'FEED_ITEMS::DELETE_FEED_ITEM_SUCCESS',
  LoadComments = 'FEED_ITEMS::LOAD_COMMENTS',
  LoadCommentsSuccess = 'FEED_ITEMS::LOAD_COMMENTS_SUCCESS',
  LoadFeedItem = 'FEED_ITEMS::LOAD_FEED_ITEM',
  LoadFeedItems = 'FEED_ITEMS::LOAD_FEED_ITEMS',
  LoadFeedItemsFailure = 'FEED_ITEMS::LOAD_FEED_ITEMS_FAILURE',
  LoadFeedItemsRequest = 'FEED_ITEMS::LOAD_FEED_ITEMS_REQUEST',
  LoadFeedItemsSuccess = 'FEED_ITEMS::LOAD_FEED_ITEMS_SUCCESS',
  LoadShareFeedItem = 'FEED_ITEMS::LOAD_SHARE_FEED_ITEM',
  LoadShareFeedItemFailure = 'FEED_ITEMS::LOAD_SHARE_FEED_ITEM_FAILURE',
  LoadShareFeedItemRequest = 'FEED_ITEMS::LOAD_SHARE_FEED_ITEM_REQUEST',
  LoadShareFeedItemSuccess = 'FEED_ITEMS::LOAD_SHARE_FEED_ITEM_SUCCESS',
  SaveChangeType = 'FEED_ITEMS::SAVE_CHANGE_TYPE',
  SaveChangeTypeFailure = 'FEED_ITEMS::SAVE_CHANGE_TYPE_FAILURE',
  SaveChangeTypeRequest = 'FEED_ITEMS::SAVE_CHANGE_TYPE_REQUEST',
  SaveChangeTypeSuccess = 'FEED_ITEMS::SAVE_CHANGE_TYPE_SUCCESS',
  SaveEditComment = 'FEED_ITEMS::SAVE_EDIT_COMMENT',
  SaveEditCommentFailure = 'FEED_ITEMS::SAVE_EDIT_COMMENT_FAILURE',
  SaveEditCommentRequest = 'FEED_ITEMS::SAVE_EDIT_COMMENT_REQUEST',
  SaveEditCommentSuccess = 'FEED_ITEMS::SAVE_EDIT_COMMENT_SUCCESS',
  SaveEditFeedItem = 'FEED_ITEMS::SAVE_EDIT_FEED_ITEM',
  SaveEditFeedItemFailure = 'FEED_ITEMS::SAVE_EDIT_FEED_ITEM_FAILURE',
  SaveEditFeedItemRequest = 'FEED_ITEMS::SAVE_EDIT_FEED_ITEM_REQUEST',
  SaveEditFeedItemSuccess = 'FEED_ITEMS::SAVE_EDIT_FEED_ITEM_SUCCESS',
  SetEditCommentIsPublic = 'FEED_ITEMS::SET_EDIT_COMMENT_IS_PUBLIC',
  SetEditCommentMarkup = 'FEED_ITEMS::SET_EDIT_COMMENT_MARKUP',
  SetEditIsPublic = 'FEED_ITEMS::SET_EDIT_IS_PUBLIC',
  SetEditLeadPriority = 'FEED_ITEMS::SET_EDIT_LEAD_PRIORITY',
  SetEditLeadType = 'FEED_ITEMS::SET_EDIT_LEAD_TYPE',
  SetEditMarkup = 'FEED_ITEMS::SET_EDIT_MARKUP',
  SetEditTitle = 'FEED_ITEMS::SET_EDIT_TITLE',
  SetSelectedPhoto = 'FEED_ITEMS::SET_SELECTED_PHOTO',
  ShareFeedItem = 'FEED_ITEMS::SHARE_FEED_ITEM',
  ShareFeedItemFailure = 'FEED_ITEMS::SHARE_FEED_ITEM_FAILURE',
  ShareFeedItemRequest = 'FEED_ITEMS::SHARE_FEED_ITEM_REQUEST',
  ShareFeedItemSuccess = 'FEED_ITEMS::SHARE_FEED_ITEM_SUCCESS',
  ShowShareFeedItemModal = 'FEED_ITEMS::SHOW_SHARE_FEED_ITEM_MODAL',
  ToggleCommentFavorite = 'FEED_ITEMS::TOGGLE_COMMENT_FAVORITE',
  ToggleCommentFavoriteSuccess = 'FEED_ITEMS::TOGGLE_COMMENT_FAVORITE_SUCCESS',
  ToggleFeedItemFavorite = 'FEED_ITEMS::SAVE_FEED_ITEM_FAVORITE',
  ToggleFeedItemFavoriteSuccess = 'FEED_ITEMS::SAVE_FEED_ITEM_FAVORITE_SUCCESS',
  ToggleShowComments = 'FEED_ITEMS::TOGGLE_SHOW_COMMENTS',
}

interface IArchiveLeadAction {
  id: string
  type: ActionTypes.ArchiveLead
}

interface IArchiveLeadFailureAction {
  error: string
  type: ActionTypes.ArchiveLeadFailure
}

export interface IArchiveLeadSuccessAction {
  id: string
  type: ActionTypes.ArchiveLeadSuccess
}

interface IBeginEditAction {
  feedItemId: string
  type: ActionTypes.BeginEdit
}

interface IBeginEditCommentAction {
  commentId: string
  feedItemId: string
  type: ActionTypes.BeginEditComment
}

interface ICancelEditAction {
  feedItemId: string
  type: ActionTypes.CancelEdit
}

interface ICancelEditCommentAction {
  commentId: string
  feedItemId: string
  type: ActionTypes.CancelEditComment
}

interface ICancelShareFeedItemModalAction {
  feedItemId: string
  type: ActionTypes.CancelShareFeedItemModal
}

interface IClearFeedItemsAction {
  type: ActionTypes.ClearFeedItems
}

interface IDeleteCommentAction {
  commentId: string
  feedItemId: string
  type: ActionTypes.DeleteComment
}

interface IDeleteCommentFailureAction {
  commentId: string
  feedItemId: string
  type: ActionTypes.DeleteCommentFailure
}

interface IDeleteCommentRequestAction {
  commentId: string
  feedItemId: string
  type: ActionTypes.DeleteCommentRequest
}

interface IDeleteCommentSuccessAction {
  commentId: string
  feedItemId: string
  type: ActionTypes.DeleteCommentSuccess
}

interface IDeleteFeedItemAction {
  feedItemId: string
  type: ActionTypes.DeleteFeedItem
}

interface IDeleteFeedItemFailureAction {
  feedItemId: string
  type: ActionTypes.DeleteFeedItemFailure
}

interface IDeleteFeedItemRequestAction {
  feedItemId: string
  type: ActionTypes.DeleteFeedItemRequest
}

export interface IDeleteFeedItemSuccessAction {
  feedItemId: string
  type: ActionTypes.DeleteFeedItemSuccess
}

interface ILoadCommentsAction {
  feedItemId: string
  type: ActionTypes.LoadComments
}

interface ILoadCommentsSuccessAction {
  comments: IComment[]
  feedItemId: string
  type: ActionTypes.LoadCommentsSuccess
}

interface ILoadFeedItemAction {
  entityId: string
  type: ActionTypes.LoadFeedItem
}

interface ILoadFeedItemsAction {
  before?: number
  count?: number
  entityId?: string
  filter?: string
  scope?: FeedItemScope
  since?: number
  type: ActionTypes.LoadFeedItems
}

interface ILoadFeedItemsFailureAction {
  error: string
  type: ActionTypes.LoadFeedItemsFailure
}

interface ILoadFeedItemsRequestAction {
  type: ActionTypes.LoadFeedItemsRequest
}

export interface ILoadFeedItemsSuccessAction {
  feedItems: IFeedItem[]
  total: number
  type: ActionTypes.LoadFeedItemsSuccess
}

interface ILoadShareFeedItemAction {
  feedItemId: string
  type: ActionTypes.LoadShareFeedItem
}

interface ILoadShareFeedItemFailureAction {
  error: string
  feedItemId: string
  type: ActionTypes.LoadShareFeedItemFailure
}

interface ILoadShareFeedItemRequestAction {
  feedItemId: string
  type: ActionTypes.LoadShareFeedItemRequest
}

interface ILoadShareFeedItemSuccessAction {
  contacts: List<IShareContact>
  feedItemId: string
  type: ActionTypes.LoadShareFeedItemSuccess
}

interface ISaveChangeTypeAction {
  feedItemId: string
  feedItemType: FeedItemType
  type: ActionTypes.SaveChangeType
}

interface ISaveChangeTypeFailureAction {
  feedItemId: string
  type: ActionTypes.SaveChangeTypeFailure
}

interface ISaveChangeTypeRequestAction {
  feedItemId: string
  type: ActionTypes.SaveChangeTypeRequest
}

interface ISaveChangeTypeSuccessAction {
  feedItemId: string
  feedItemType: FeedItemType
  type: ActionTypes.SaveChangeTypeSuccess
}

interface ISaveEditCommentAction {
  feedItemId: string
  comment: IComment
  type: ActionTypes.SaveEditComment
}

interface ISaveEditCommentFailureAction {
  feedItemId: string
  commentId: string
  type: ActionTypes.SaveEditCommentFailure
}

interface ISaveEditCommentRequestAction {
  feedItemId: string
  commentId: string
  type: ActionTypes.SaveEditCommentRequest
}

interface ISaveEditCommentSuccessAction {
  feedItemId: string
  comment: Comment
  type: ActionTypes.SaveEditCommentSuccess
}

interface ISaveEditFeedItemAction {
  feedItem: IFeedItem
  type: ActionTypes.SaveEditFeedItem
}

interface ISaveEditFeedItemFailureAction {
  feedItemId: string
  type: ActionTypes.SaveEditFeedItemFailure
}

interface ISaveEditFeedItemRequestAction {
  feedItemId: string
  type: ActionTypes.SaveEditFeedItemRequest
}

export interface ISaveEditFeedItemSuccessAction {
  feedItem: FeedItem
  type: ActionTypes.SaveEditFeedItemSuccess
}

interface ISetEditCommentIsPublicAction {
  commentId: string
  feedItemId: string
  isPublic: boolean
  type: ActionTypes.SetEditCommentIsPublic
}

interface ISetEditCommentMarkupAction {
  commentId: string
  feedItemId: string
  markupState: EditorState
  type: ActionTypes.SetEditCommentMarkup
}

interface ISetEditIsPublicAction {
  feedItemId: string
  isPublic: boolean
  type: ActionTypes.SetEditIsPublic
}

interface ISetEditLeadPriorityAction {
  feedItemId: string
  type: ActionTypes.SetEditLeadPriority
  value: string
}

interface ISetEditLeadTypeAction {
  feedItemId: string
  type: ActionTypes.SetEditLeadType
  value: string
}

interface ISetEditMarkupAction {
  feedItemId: string
  markupState: EditorState
  type: ActionTypes.SetEditMarkup
}

interface ISetEditTitleAction {
  feedItemId: string
  title: string
  type: ActionTypes.SetEditTitle
}

interface ISetSelectedPhotoAction {
  feedItemId: string
  selectedPhotoId: string
  type: ActionTypes.SetSelectedPhoto
}

interface IShareFeedItemAction {
  comment: string
  contacts: List<IShareContact>
  shareInfo: IShareFeedItem
  type: ActionTypes.ShareFeedItem
}

interface IShareFeedItemFailureAction {
  error: string
  feedItemId: string
  type: ActionTypes.ShareFeedItemFailure
}

interface IShareFeedItemRequestAction {
  feedItemId: string
  type: ActionTypes.ShareFeedItemRequest
}

interface IShareFeedItemSuccessAction {
  feedItemId: string
  type: ActionTypes.ShareFeedItemSuccess
}

interface IShowShareFeedItemModalAction {
  feedItemId: string
  type: ActionTypes.ShowShareFeedItemModal
}

interface IToggleCommentFavoriteAction {
  commentId: string
  favorite: boolean
  feedItemId: string
  type: ActionTypes.ToggleCommentFavorite
  userId: string
}

interface IToggleCommentFavoriteSuccessAction {
  commentId: string
  favorite: boolean
  feedItemId: string
  type: ActionTypes.ToggleCommentFavoriteSuccess
  userId: string
}

interface IToggleFeedItemFavoriteAction {
  favorite: boolean
  feedItemId: string
  type: ActionTypes.ToggleFeedItemFavorite
  userId: string
}

interface IToggleFeedItemFavoriteSuccessAction {
  favorite: boolean
  feedItemId: string
  type: ActionTypes.ToggleFeedItemFavoriteSuccess
  userId: string
}

interface IToggleShowCommentsAction {
  feedItemId: string
  show: boolean
  type: ActionTypes.ToggleShowComments
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | IArchiveLeadFailureAction
  | IArchiveLeadSuccessAction
  | IBeginEditAction
  | IBeginEditCommentAction
  | ICancelEditAction
  | ICancelEditCommentAction
  | ICancelShareFeedItemModalAction
  | IClearFeedItemsAction
  | ICreateFeedItemSuccessAction
  | IDeleteCommentFailureAction
  | IDeleteCommentRequestAction
  | IDeleteCommentSuccessAction
  | IDeleteFeedItemFailureAction
  | IDeleteFeedItemRequestAction
  | IDeleteFeedItemSuccessAction
  | ILoadCommentsSuccessAction
  | ILoadFeedItemsFailureAction
  | ILoadFeedItemsRequestAction
  | ILoadFeedItemsSuccessAction
  | ILoadShareFeedItemFailureAction
  | ILoadShareFeedItemRequestAction
  | ILoadShareFeedItemSuccessAction
  | ISaveChangeTypeFailureAction
  | ISaveChangeTypeRequestAction
  | ISaveChangeTypeSuccessAction
  | ISaveCommentSuccessAction
  | ISaveEditCommentFailureAction
  | ISaveEditCommentRequestAction
  | ISaveEditCommentSuccessAction
  | ISaveEditFeedItemFailureAction
  | ISaveEditFeedItemRequestAction
  | ISaveEditFeedItemSuccessAction
  | ISetEditCommentIsPublicAction
  | ISetEditCommentMarkupAction
  | ISetEditIsPublicAction
  | ISetEditLeadPriorityAction
  | ISetEditLeadTypeAction
  | ISetEditMarkupAction
  | ISetEditTitleAction
  | ISetSelectedPhotoAction
  | IShareFeedItemFailureAction
  | IShareFeedItemRequestAction
  | IShareFeedItemSuccessAction
  | IShowShareFeedItemModalAction
  | IToggleCommentFavoriteSuccessAction
  | IToggleFeedItemFavoriteSuccessAction
  | IToggleShowCommentsAction

// -------------------------
// ACTION CREATORS
// -------------------------
export interface IClearFeedItemsActionCreator {
  clearFeedItems: () => IClearFeedItemsAction
}

export interface ILoadFeedItemOptions {
  before?: number
  count?: number
  entityId?: string
  filter?: string
  scope?: FeedItemScope
  since?: number
}

function archiveLead(id: string): IArchiveLeadAction {
  return {
    id,
    type: ActionTypes.ArchiveLead,
  }
}

function archiveLeadFailure(error: string): IArchiveLeadFailureAction {
  return {
    error,
    type: ActionTypes.ArchiveLeadFailure,
  }
}

function archiveLeadSuccess(id: string): IArchiveLeadSuccessAction {
  return {
    id,
    type: ActionTypes.ArchiveLeadSuccess,
  }
}

function beginEdit(feedItemId: string): IBeginEditAction {
  return {
    feedItemId,
    type: ActionTypes.BeginEdit,
  }
}

function beginEditComment(
  feedItemId: string,
  commentId: string,
): IBeginEditCommentAction {
  return {
    commentId,
    feedItemId,
    type: ActionTypes.BeginEditComment,
  }
}

function cancelEdit(feedItemId: string): ICancelEditAction {
  return {
    feedItemId,
    type: ActionTypes.CancelEdit,
  }
}

function cancelEditComment(
  feedItemId: string,
  commentId: string,
): ICancelEditCommentAction {
  return {
    commentId,
    feedItemId,
    type: ActionTypes.CancelEditComment,
  }
}

function cancelShareFeedItemModal(
  feedItemId: string,
): ICancelShareFeedItemModalAction {
  return {
    feedItemId,
    type: ActionTypes.CancelShareFeedItemModal,
  }
}

function clearFeedItems(): IClearFeedItemsAction {
  return { type: ActionTypes.ClearFeedItems }
}

function deleteComment(
  feedItemId: string,
  commentId: string,
): IDeleteCommentAction {
  return {
    commentId,
    feedItemId,
    type: ActionTypes.DeleteComment,
  }
}

function deleteCommentFailure(
  feedItemId: string,
  commentId: string,
): IDeleteCommentFailureAction {
  return {
    commentId,
    feedItemId,
    type: ActionTypes.DeleteCommentFailure,
  }
}

function deleteCommentRequest(
  feedItemId: string,
  commentId: string,
): IDeleteCommentRequestAction {
  return {
    commentId,
    feedItemId,
    type: ActionTypes.DeleteCommentRequest,
  }
}

function deleteCommentSuccess(
  feedItemId: string,
  commentId: string,
): IDeleteCommentSuccessAction {
  return {
    commentId,
    feedItemId,
    type: ActionTypes.DeleteCommentSuccess,
  }
}

function deleteFeedItem(feedItemId: string): IDeleteFeedItemAction {
  return {
    feedItemId,
    type: ActionTypes.DeleteFeedItem,
  }
}

function deleteFeedItemFailure(
  feedItemId: string,
): IDeleteFeedItemFailureAction {
  return {
    feedItemId,
    type: ActionTypes.DeleteFeedItemFailure,
  }
}

function deleteFeedItemRequest(
  feedItemId: string,
): IDeleteFeedItemRequestAction {
  return {
    feedItemId,
    type: ActionTypes.DeleteFeedItemRequest,
  }
}

function deleteFeedItemSuccess(
  feedItemId: string,
): IDeleteFeedItemSuccessAction {
  return {
    feedItemId,
    type: ActionTypes.DeleteFeedItemSuccess,
  }
}

function loadComments(feedItemId: string): ILoadCommentsAction {
  return {
    feedItemId,
    type: ActionTypes.LoadComments,
  }
}

function loadCommentsSuccess(
  feedItemId: string,
  comments: IComment[],
): ILoadCommentsSuccessAction {
  return {
    comments,
    feedItemId,
    type: ActionTypes.LoadCommentsSuccess,
  }
}

function loadFeedItem(entityId: string): ILoadFeedItemAction {
  return {
    entityId,
    type: ActionTypes.LoadFeedItem,
  }
}

function loadFeedItems(options?: ILoadFeedItemOptions): ILoadFeedItemsAction {
  return {
    ...options,
    type: ActionTypes.LoadFeedItems,
  }
}

function loadFeedItemsFailure(error: string): ILoadFeedItemsFailureAction {
  return {
    error,
    type: ActionTypes.LoadFeedItemsFailure,
  }
}

function loadFeedItemsRequest(): ILoadFeedItemsRequestAction {
  return { type: ActionTypes.LoadFeedItemsRequest }
}

function loadFeedItemsSuccess(
  feedItems: IFeedItem[],
  total: number,
): ILoadFeedItemsSuccessAction {
  return {
    feedItems,
    total,
    type: ActionTypes.LoadFeedItemsSuccess,
  }
}

function loadShareFeedItem(feedItemId: string): ILoadShareFeedItemAction {
  return {
    feedItemId,
    type: ActionTypes.LoadShareFeedItem,
  }
}

function loadShareFeedItemFailure(
  feedItemId: string,
  error: string,
): ILoadShareFeedItemFailureAction {
  return {
    error,
    feedItemId,
    type: ActionTypes.LoadShareFeedItemFailure,
  }
}

function loadShareFeedItemRequest(
  feedItemId: string,
): ILoadShareFeedItemRequestAction {
  return {
    feedItemId,
    type: ActionTypes.LoadShareFeedItemRequest,
  }
}

function loadShareFeedItemSuccess(
  feedItemId: string,
  contacts: List<IShareContact>,
): ILoadShareFeedItemSuccessAction {
  return {
    contacts,
    feedItemId,
    type: ActionTypes.LoadShareFeedItemSuccess,
  }
}

function saveChangeType(
  feedItemId: string,
  feedItemType: FeedItemType,
): ISaveChangeTypeAction {
  return {
    feedItemId,
    feedItemType,
    type: ActionTypes.SaveChangeType,
  }
}

function saveChangeTypeFailure(
  feedItemId: string,
): ISaveChangeTypeFailureAction {
  return {
    feedItemId,
    type: ActionTypes.SaveChangeTypeFailure,
  }
}

function saveChangeTypeRequest(
  feedItemId: string,
): ISaveChangeTypeRequestAction {
  return {
    feedItemId,
    type: ActionTypes.SaveChangeTypeRequest,
  }
}

function saveChangeTypeSuccess(
  feedItemId: string,
  feedItemType: FeedItemType,
): ISaveChangeTypeSuccessAction {
  return {
    feedItemId,
    feedItemType,
    type: ActionTypes.SaveChangeTypeSuccess,
  }
}

function saveEditComment(
  feedItemId: string,
  comment: IComment,
): ISaveEditCommentAction {
  return {
    comment,
    feedItemId,
    type: ActionTypes.SaveEditComment,
  }
}

function saveEditCommentFailure(
  feedItemId: string,
  commentId: string,
): ISaveEditCommentFailureAction {
  return {
    commentId,
    feedItemId,
    type: ActionTypes.SaveEditCommentFailure,
  }
}

function saveEditCommentRequest(
  feedItemId: string,
  commentId: string,
): ISaveEditCommentRequestAction {
  return {
    commentId,
    feedItemId,
    type: ActionTypes.SaveEditCommentRequest,
  }
}

function saveEditCommentSuccess(
  feedItemId: string,
  comment: Comment,
): ISaveEditCommentSuccessAction {
  return {
    comment,
    feedItemId,
    type: ActionTypes.SaveEditCommentSuccess,
  }
}

function saveEditFeedItem(feedItem: IFeedItem): ISaveEditFeedItemAction {
  return {
    feedItem,
    type: ActionTypes.SaveEditFeedItem,
  }
}

function saveEditFeedItemFailure(
  feedItemId: string,
): ISaveEditFeedItemFailureAction {
  return {
    feedItemId,
    type: ActionTypes.SaveEditFeedItemFailure,
  }
}

function saveEditFeedItemRequest(
  feedItemId: string,
): ISaveEditFeedItemRequestAction {
  return {
    feedItemId,
    type: ActionTypes.SaveEditFeedItemRequest,
  }
}

function saveEditFeedItemSuccess(
  feedItem: FeedItem,
): ISaveEditFeedItemSuccessAction {
  return {
    feedItem,
    type: ActionTypes.SaveEditFeedItemSuccess,
  }
}

function setEditCommentIsPublic(
  feedItemId: string,
  commentId: string,
  isPublic: boolean,
): ISetEditCommentIsPublicAction {
  return {
    commentId,
    feedItemId,
    isPublic,
    type: ActionTypes.SetEditCommentIsPublic,
  }
}

function setEditCommentMarkup(
  feedItemId: string,
  commentId: string,
  markupState: EditorState,
): ISetEditCommentMarkupAction {
  return {
    commentId,
    feedItemId,
    markupState,
    type: ActionTypes.SetEditCommentMarkup,
  }
}

function setEditIsPublic(
  feedItemId: string,
  isPublic: boolean,
): ISetEditIsPublicAction {
  return {
    feedItemId,
    isPublic,
    type: ActionTypes.SetEditIsPublic,
  }
}

function setEditLeadPriority(
  feedItemId: string,
  value: string,
): ISetEditLeadPriorityAction {
  return {
    feedItemId,
    type: ActionTypes.SetEditLeadPriority,
    value,
  }
}

function setEditLeadType(
  feedItemId: string,
  value: string,
): ISetEditLeadTypeAction {
  return {
    feedItemId,
    type: ActionTypes.SetEditLeadType,
    value,
  }
}

function setEditMarkup(
  feedItemId: string,
  markupState: EditorState,
): ISetEditMarkupAction {
  return {
    feedItemId,
    markupState,
    type: ActionTypes.SetEditMarkup,
  }
}

function setEditTitle(feedItemId: string, title: string): ISetEditTitleAction {
  return {
    feedItemId,
    title,
    type: ActionTypes.SetEditTitle,
  }
}

function setSelectedPhoto(
  feedItemId: string,
  selectedPhotoId: string,
): ISetSelectedPhotoAction {
  return {
    feedItemId,
    selectedPhotoId,
    type: ActionTypes.SetSelectedPhoto,
  }
}

function shareFeedItem(
  shareInfo: IShareFeedItem,
  contacts: List<IShareContact>,
  comment: string,
): IShareFeedItemAction {
  return {
    comment,
    contacts,
    shareInfo,
    type: ActionTypes.ShareFeedItem,
  }
}

function shareFeedItemFailure(
  feedItemId: string,
  error: string,
): IShareFeedItemFailureAction {
  return {
    error,
    feedItemId,
    type: ActionTypes.ShareFeedItemFailure,
  }
}

function shareFeedItemRequest(feedItemId: string): IShareFeedItemRequestAction {
  return {
    feedItemId,
    type: ActionTypes.ShareFeedItemRequest,
  }
}

function shareFeedItemSuccess(feedItemId: string): IShareFeedItemSuccessAction {
  return {
    feedItemId,
    type: ActionTypes.ShareFeedItemSuccess,
  }
}

function showShareFeedItemModal(
  feedItemId: string,
): IShowShareFeedItemModalAction {
  return {
    feedItemId,
    type: ActionTypes.ShowShareFeedItemModal,
  }
}

function toggleCommentFavorite(
  feedItemId: string,
  commentId: string,
  userId: string,
  favorite: boolean,
): IToggleCommentFavoriteAction {
  return {
    commentId,
    favorite,
    feedItemId,
    type: ActionTypes.ToggleCommentFavorite,
    userId,
  }
}

function toggleCommentFavoriteSuccess(
  feedItemId: string,
  commentId: string,
  userId: string,
  favorite: boolean,
): IToggleCommentFavoriteSuccessAction {
  return {
    commentId,
    favorite,
    feedItemId,
    type: ActionTypes.ToggleCommentFavoriteSuccess,
    userId,
  }
}

function toggleFeedItemFavorite(
  feedItemId: string,
  userId: string,
  favorite: boolean,
): IToggleFeedItemFavoriteAction {
  return {
    favorite,
    feedItemId,
    type: ActionTypes.ToggleFeedItemFavorite,
    userId,
  }
}

function toggleFeedItemFavoriteSuccess(
  feedItemId: string,
  userId: string,
  favorite: boolean,
): IToggleFeedItemFavoriteSuccessAction {
  return {
    favorite,
    feedItemId,
    type: ActionTypes.ToggleFeedItemFavoriteSuccess,
    userId,
  }
}

function toggleShowComments(
  feedItemId: string,
  show: boolean,
): IToggleShowCommentsAction {
  return {
    feedItemId,
    show,
    type: ActionTypes.ToggleShowComments,
  }
}

export const actionCreators = {
  archiveLead,
  beginEdit,
  cancelEdit,
  clearFeedItems,
  deleteFeedItem,
  loadComments,
  loadFeedItem,
  loadFeedItems,
  saveChangeType,
  saveEditFeedItem,
  setEditIsPublic,
  setEditLeadPriority,
  setEditLeadType,
  setEditMarkup,
  setEditTitle,
  setSelectedPhoto,
  showShareFeedItemModal,
  toggleFeedItemFavorite,
  toggleShowComments,
}

export const commentActionCreators = {
  beginEditComment,
  cancelEditComment,
  deleteComment,
  saveEditComment,
  setEditCommentIsPublic,
  setEditCommentMarkup,
  toggleCommentFavorite,
}

export const shareActionCreators = {
  cancelShareFeedItemModal,
  loadShareFeedItem,
  shareFeedItem,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<FeedItemsState> = (
  state: FeedItemsState = new FeedItemsState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case ActionTypes.ArchiveLeadFailure:
      return state.set('error', action.error) as FeedItemsState

    case ActionTypes.ArchiveLeadSuccess:
      if (state.hasIn(['items', action.id])) {
        return state.setIn(
          ['items', action.id, 'isArchived'],
          true,
        ) as FeedItemsState
      } else {
        return state
      }

    case ActionTypes.BeginEdit: {
      const feedItem = state.getFeedItem(action.feedItemId)
      if (feedItem) {
        return state
          .setIn(['items', action.feedItemId, 'isEditing'], true)
          .setIn(
            ['items', action.feedItemId, 'editState'],
            FeedItemEditState.fromFeedItem(feedItem),
          ) as FeedItemsState
      } else {
        return state
      }
    }

    case ActionTypes.BeginEditComment: {
      const commentIndex = state.getCommentIndex(
        action.feedItemId,
        action.commentId,
      )
      if (commentIndex >= 0) {
        const comment = state.getComment(
          action.feedItemId,
          action.commentId,
        ) as Comment
        return state
          .setIn(
            ['items', action.feedItemId, 'comments', commentIndex, 'editState'],
            CommentEditState.fromComment(comment),
          )
          .setIn(
            ['items', action.feedItemId, 'comments', commentIndex, 'isEditing'],
            true,
          ) as FeedItemsState
      } else {
        return state
      }
    }

    case ActionTypes.CancelEdit:
      if (state.hasIn(['items', action.feedItemId])) {
        return state
          .setIn(['items', action.feedItemId, 'isEditing'], false)
          .setIn(
            ['items', action.feedItemId, 'editState'],
            undefined,
          ) as FeedItemsState
      } else {
        return state
      }

    case ActionTypes.CancelEditComment: {
      const commentIndex = state.getCommentIndex(
        action.feedItemId,
        action.commentId,
      )
      if (commentIndex >= 0) {
        return state
          .setIn(
            ['items', action.feedItemId, 'comments', commentIndex, 'editState'],
            undefined,
          )
          .setIn(
            ['items', action.feedItemId, 'comments', commentIndex, 'isEditing'],
            false,
          ) as FeedItemsState
      } else {
        return state
      }
    }

    case ActionTypes.CancelShareFeedItemModal:
      return state.deleteIn(['shareItems', action.feedItemId]) as FeedItemsState

    case ActionTypes.ClearFeedItems:
      return state.set('items', Map<string, FeedItem>()) as FeedItemsState

    case FeedItemEditorActionTypes.CreateFeedItemSuccess:
      return state.setIn(
        ['items', action.feedItem.id],
        action.feedItem,
      ) as FeedItemsState

    case ActionTypes.DeleteCommentFailure: {
      const commentIndex = state.getCommentIndex(
        action.feedItemId,
        action.commentId,
      )
      if (commentIndex >= 0) {
        return state.setIn(
          ['items', action.feedItemId, 'comments', commentIndex, 'isSaving'],
          false,
        ) as FeedItemsState
      } else {
        return state
      }
    }

    case ActionTypes.DeleteCommentRequest: {
      const commentIndex = state.getCommentIndex(
        action.feedItemId,
        action.commentId,
      )
      if (commentIndex >= 0) {
        return state.setIn(
          ['items', action.feedItemId, 'comments', commentIndex, 'isSaving'],
          true,
        ) as FeedItemsState
      } else {
        return state
      }
    }

    case ActionTypes.DeleteCommentSuccess: {
      const feedItem = state.getFeedItem(action.feedItemId)
      const commentIndex = state.getCommentIndex(
        action.feedItemId,
        action.commentId,
      )
      if (feedItem && commentIndex >= 0) {
        return state
          .deleteIn(['items', action.feedItemId, 'comments', commentIndex])
          .setIn(
            ['items', action.feedItemId, 'totalComments'],
            feedItem.totalComments - 1,
          ) as FeedItemsState
      } else {
        return state
      }
    }

    case ActionTypes.DeleteFeedItemFailure:
      if (state.hasIn(['items', action.feedItemId])) {
        return state.setIn(
          ['items', action.feedItemId, 'isSaving'],
          false,
        ) as FeedItemsState
      } else {
        return state
      }

    case ActionTypes.DeleteFeedItemRequest:
      if (state.hasIn(['items', action.feedItemId])) {
        return state.setIn(
          ['items', action.feedItemId, 'isSaving'],
          true,
        ) as FeedItemsState
      } else {
        return state
      }

    case ActionTypes.DeleteFeedItemSuccess:
      if (state.hasIn(['items', action.feedItemId])) {
        return state.deleteIn(['items', action.feedItemId]) as FeedItemsState
      } else {
        return state
      }

    case ActionTypes.LoadCommentsSuccess:
      if (state.hasIn(['items', action.feedItemId])) {
        return state.setIn(
          ['items', action.feedItemId, 'comments'],
          List(action.comments.map((c) => Comment.fromJS(c))),
        ) as FeedItemsState
      } else {
        return state
      }

    case ActionTypes.LoadFeedItemsFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false) as FeedItemsState

    case ActionTypes.LoadFeedItemsRequest:
      return state.set('isLoading', true) as FeedItemsState

    case ActionTypes.LoadFeedItemsSuccess:
      return state
        .set('error', undefined)
        .set('isLoading', false)
        .mergeDeepIn(
          ['items'],
          toFeedItemMap(action.feedItems, state.items),
        ) as FeedItemsState

    case ActionTypes.LoadShareFeedItemFailure:
      if (state.hasIn(['shareItems', action.feedItemId])) {
        return state.setIn(
          ['shareItems', action.feedItemId, 'isLoading'],
          false,
        ) as FeedItemsState
      } else {
        return state
      }

    case ActionTypes.LoadShareFeedItemRequest:
      if (state.hasIn(['shareItems', action.feedItemId])) {
        return state.setIn(
          ['shareItems', action.feedItemId, 'isLoading'],
          true,
        ) as FeedItemsState
      } else {
        return state
      }

    case ActionTypes.LoadShareFeedItemSuccess:
      if (state.hasIn(['shareItems', action.feedItemId])) {
        return state
          .setIn(['shareItems', action.feedItemId, 'contacts'], action.contacts)
          .setIn(
            ['shareItems', action.feedItemId, 'isLoading'],
            false,
          ) as FeedItemsState
      } else {
        return state
      }

    case ActionTypes.SaveChangeTypeFailure:
      if (state.hasIn(['items', action.feedItemId])) {
        return state.setIn(
          ['items', action.feedItemId, 'isSaving'],
          false,
        ) as FeedItemsState
      } else {
        return state
      }

    case ActionTypes.SaveChangeTypeRequest:
      if (state.hasIn(['items', action.feedItemId])) {
        return state.setIn(
          ['items', action.feedItemId, 'isSaving'],
          true,
        ) as FeedItemsState
      } else {
        return state
      }

    case ActionTypes.SaveChangeTypeSuccess:
      if (state.hasIn(['items', action.feedItemId])) {
        return state
          .setIn(['items', action.feedItemId, 'isSaving'], false)
          .setIn(
            ['items', action.feedItemId, 'type'],
            action.feedItemType,
          ) as FeedItemsState
      } else {
        return state
      }

    case CommentEditorActionTypes.SaveCommentSuccess: {
      const feedItem = state.getFeedItem(action.entityId)
      if (feedItem) {
        const itemComments = feedItem.comments
          ? feedItem.comments.push(action.comment)
          : List<Comment>([action.comment])

        return state
          .setIn(['items', action.entityId, 'comments'], itemComments)
          .setIn(
            ['items', action.entityId, 'totalComments'],
            feedItem.totalComments + 1,
          ) as FeedItemsState
      } else {
        return state
      }
    }

    case ActionTypes.SaveEditCommentFailure: {
      const commentIndex = state.getCommentIndex(
        action.feedItemId,
        action.commentId,
      )
      if (commentIndex >= 0) {
        return state.setIn(
          ['items', action.feedItemId, 'comments', commentIndex, 'isSaving'],
          false,
        ) as FeedItemsState
      } else {
        return state
      }
    }

    case ActionTypes.SaveEditCommentRequest: {
      const commentIndex = state.getCommentIndex(
        action.feedItemId,
        action.commentId,
      )
      if (commentIndex >= 0) {
        return state.setIn(
          ['items', action.feedItemId, 'comments', commentIndex, 'isSaving'],
          true,
        ) as FeedItemsState
      } else {
        return state
      }
    }

    case ActionTypes.SaveEditCommentSuccess: {
      const commentIndex = state.getCommentIndex(
        action.feedItemId,
        action.comment.id,
      )
      if (commentIndex >= 0) {
        return state.setIn(
          ['items', action.feedItemId, 'comments', commentIndex],
          action.comment,
        ) as FeedItemsState
      } else {
        return state
      }
    }

    case ActionTypes.SaveEditFeedItemFailure:
      if (state.hasIn(['items', action.feedItemId])) {
        return state.setIn(
          ['items', action.feedItemId, 'isSaving'],
          false,
        ) as FeedItemsState
      } else {
        return state
      }

    case ActionTypes.SaveEditFeedItemRequest:
      if (state.hasIn(['items', action.feedItemId])) {
        return state.setIn(
          ['items', action.feedItemId, 'isSaving'],
          true,
        ) as FeedItemsState
      } else {
        return state
      }

    case ActionTypes.SaveEditFeedItemSuccess:
      return state.setIn(
        ['items', action.feedItem.id],
        action.feedItem,
      ) as FeedItemsState

    case ActionTypes.SetEditCommentIsPublic: {
      const commentIndex = state.getCommentIndex(
        action.feedItemId,
        action.commentId,
      )
      if (commentIndex >= 0) {
        return state.setIn(
          [
            'items',
            action.feedItemId,
            'comments',
            commentIndex,
            'editState',
            'isPublic',
          ],
          action.isPublic,
        ) as FeedItemsState
      } else {
        return state
      }
    }

    case ActionTypes.SetEditCommentMarkup: {
      const commentIndex = state.getCommentIndex(
        action.feedItemId,
        action.commentId,
      )
      if (commentIndex >= 0) {
        return state.setIn(
          [
            'items',
            action.feedItemId,
            'comments',
            commentIndex,
            'editState',
            'markupState',
          ],
          action.markupState,
        ) as FeedItemsState
      } else {
        return state
      }
    }

    case ActionTypes.SetEditIsPublic:
      if (state.hasIn(['items', action.feedItemId, 'editState'])) {
        return state.setIn(
          ['items', action.feedItemId, 'editState', 'isPublic'],
          action.isPublic,
        ) as FeedItemsState
      } else {
        return state
      }

    case ActionTypes.SetEditLeadPriority:
      if (state.hasIn(['items', action.feedItemId, 'editState'])) {
        return state.setIn(
          ['items', action.feedItemId, 'editState', 'leadPriority'],
          action.value,
        ) as FeedItemsState
      } else {
        return state
      }

    case ActionTypes.SetEditLeadType:
      if (state.hasIn(['items', action.feedItemId, 'editState'])) {
        return state.setIn(
          ['items', action.feedItemId, 'editState', 'leadType'],
          action.value,
        ) as FeedItemsState
      } else {
        return state
      }

    case ActionTypes.SetEditMarkup:
      if (state.hasIn(['items', action.feedItemId, 'editState'])) {
        return state.setIn(
          ['items', action.feedItemId, 'editState', 'markupState'],
          action.markupState,
        ) as FeedItemsState
      } else {
        return state
      }

    case ActionTypes.SetEditTitle:
      if (state.hasIn(['items', action.feedItemId, 'editState'])) {
        return state.setIn(
          ['items', action.feedItemId, 'editState', 'title'],
          action.title,
        ) as FeedItemsState
      } else {
        return state
      }

    case ActionTypes.SetSelectedPhoto:
      if (state.hasIn(['items', action.feedItemId])) {
        return state.setIn(
          ['items', action.feedItemId, 'selectedPhoto'],
          action.selectedPhotoId,
        ) as FeedItemsState
      } else {
        return state
      }

    case ActionTypes.ShareFeedItemFailure:
      if (state.hasIn(['shareItems', action.feedItemId])) {
        return state.setIn(
          ['shareItems', action.feedItemId, 'isSaving'],
          false,
        ) as FeedItemsState
      } else {
        return state
      }

    case ActionTypes.ShareFeedItemRequest:
      if (state.hasIn(['shareItems', action.feedItemId])) {
        return state.setIn(
          ['shareItems', action.feedItemId, 'isSaving'],
          true,
        ) as FeedItemsState
      } else {
        return state
      }

    case ActionTypes.ShareFeedItemSuccess:
      if (state.hasIn(['shareItems', action.feedItemId])) {
        return state.deleteIn([
          'shareItems',
          action.feedItemId,
        ]) as FeedItemsState
      } else {
        return state
      }

    case ActionTypes.ShowShareFeedItemModal:
      return state.setIn(
        ['shareItems', action.feedItemId],
        new ShareFeedItem({ feedItemId: action.feedItemId }),
      ) as FeedItemsState

    case ActionTypes.ToggleCommentFavoriteSuccess: {
      const comment = state.getComment(action.feedItemId, action.commentId)
      if (comment) {
        const commentIndex = state.getCommentIndex(
          action.feedItemId,
          action.commentId,
        )
        let favorites = comment.hasStarred
        if (action.favorite === false) {
          favorites = favorites.filter((f) => f !== action.userId).toList()
        } else if (!favorites.includes(action.userId)) {
          favorites = favorites.push(action.userId)
        }
        return state.setIn(
          ['items', action.feedItemId, 'comments', commentIndex, 'hasStarred'],
          favorites,
        ) as FeedItemsState
      } else {
        return state
      }
    }

    case ActionTypes.ToggleFeedItemFavoriteSuccess: {
      const feedItem = state.getFeedItem(action.feedItemId)
      if (feedItem) {
        let favorites = feedItem.hasStarred
        if (action.favorite === false) {
          favorites = favorites.filter((f) => f !== action.userId).toList()
        } else if (!favorites.includes(action.userId)) {
          favorites = favorites.push(action.userId)
        }
        return state.setIn(
          ['items', action.feedItemId, 'hasStarred'],
          favorites,
        ) as FeedItemsState
      } else {
        return state
      }
    }

    case ActionTypes.ToggleShowComments:
      if (state.hasIn(['items', action.feedItemId])) {
        return state.setIn(
          ['items', action.feedItemId, 'showComments'],
          action.show,
        ) as FeedItemsState
      } else {
        return state
      }

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

interface IFeedItemMap {
  [key: string]: FeedItem
}

function toFeedItemMap(
  feedItems: IFeedItem[],
  existingItems: Map<string, FeedItem>,
): Map<string, FeedItem> {
  const newFeedItems = feedItems.reduce<IFeedItemMap>(
    (map: IFeedItemMap, item) => {
      let newItem = FeedItem.fromJS(item)

      if (existingItems.has(newItem.id)) {
        newItem = newItem.set(
          'showComments',
          existingItems.getIn([newItem.id, 'showComments']),
        ) as FeedItem
      }

      map[item.id] = newItem

      return map
    },
    {},
  )
  return Map<string, FeedItem>(newFeedItems)
}

// -------------------------
// SAGAS
// -------------------------
function* archiveLeadSaga(action: IArchiveLeadAction) {
  try {
    const baseUrl = `/api/Leads/${action.id}/archive`

    const opts = {
      method: RequestMethod.PUT,
    }

    const result = yield makeAuthenticatedRequest(baseUrl, opts)
    if (result.success === true) {
      yield put(successToast('Success', 'Lead successfully archived'))
      yield put(archiveLeadSuccess(action.id))
    } else {
      yield put(errorToast('Oops', result.message))
      yield put(archiveLeadFailure(result.message))
    }
  } catch (err) {
    yield put(errorToast('Oops', err.message))
    yield put(archiveLeadFailure(err.message))
  }
}

function* deleteCommentSaga(action: IDeleteCommentAction) {
  try {
    yield put(deleteCommentRequest(action.feedItemId, action.commentId))

    const url = `/api/FeedItems/${action.feedItemId}/Comments/${action.commentId}`
    const opts: IRequestOptions = {
      method: RequestMethod.DELETE,
    }

    const result = yield makeAuthenticatedRequest(url, opts)

    if (result.success === true) {
      yield put(deleteCommentSuccess(action.feedItemId, action.commentId))
      yield put(successToast('Success', 'Comment deleted'))
    } else {
      yield put(deleteCommentFailure(action.feedItemId, action.commentId))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(deleteCommentFailure(action.feedItemId, action.commentId))
    yield put(errorToast('Oops!', err.message))
  }
}

function* deleteFeedItemSaga(action: IDeleteFeedItemAction) {
  try {
    yield put(deleteFeedItemRequest(action.feedItemId))

    const url = `/api/FeedItems/${action.feedItemId}`
    const opts: IRequestOptions = {
      method: RequestMethod.DELETE,
    }

    const result = yield makeAuthenticatedRequest(url, opts)

    if (result.success === true) {
      yield put(deleteFeedItemSuccess(action.feedItemId))
      yield put(successToast('Success', 'Feed item deleted'))
    } else {
      yield put(deleteFeedItemFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(deleteFeedItemFailure(action.feedItemId))
    yield put(errorToast('Oops!', err.message))
  }
}

function* loadCommentsSaga(action: ILoadCommentsAction) {
  try {
    const commentsUrl = `/api/FeedItems/${action.feedItemId}/Comments`
    const result = yield makeAuthenticatedRequest(commentsUrl)
    if (result.success === true) {
      yield put(loadCommentsSuccess(action.feedItemId, result.results))
    } else {
      yield put(errorToast('Oops!', result.message))
    }
  } catch (error) {
    yield put(errorToast('Oops!', error.message))
  }
}

export function* loadFeedItemSaga(action: ILoadFeedItemAction) {
  try {
    yield put(loadFeedItemsRequest())

    const feedUrl = `/api/FeedItems/${action.entityId}`

    const result = yield makeAuthenticatedRequest(feedUrl)

    if (result.success === true) {
      const successAction = loadFeedItemsSuccess([result.entity as FeedItem], 1)
      yield put(successAction)
      return successAction
    } else {
      yield put(errorToast('Oops!', result.message))
      yield put(loadFeedItemsFailure(result.message))
    }
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
    yield put(loadFeedItemsFailure(err.message))
  }
}

export function* loadFeedItemsSaga(action: ILoadFeedItemsAction) {
  try {
    yield put(loadFeedItemsRequest())

    const baseUrl =
      !action.filter && !action.entityId && action.scope === FeedItemScope.Mine
        ? '/api/FeedItems/My'
        : '/api/FeedItems'

    const query: string[] = []

    if (action.count !== undefined && action.count > 0) {
      query.push(`count=${action.count}`)
    }

    if (action.entityId) {
      query.push(`id=${encodeURIComponent(action.entityId)}`)
    }

    if (action.filter) {
      query.push(`filter=${encodeURIComponent(action.filter)}`)
    }

    if (action.before) {
      query.push(`before=${action.before}`)
    }

    if (action.since) {
      query.push(`since=${action.since}`)
    }

    const feedUrl = `${baseUrl}${query.length > 0 ? `?${query.join('&')}` : ''}`

    const result = yield makeAuthenticatedRequest(feedUrl)

    if (result.success === true) {
      const successAction = loadFeedItemsSuccess(
        result.results,
        result.paging.total,
      )
      yield put(successAction)
      return successAction
    } else {
      yield put(errorToast('Oops!', result.message))
      yield put(loadFeedItemsFailure(result.message))
    }
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
    yield put(loadFeedItemsFailure(err.message))
  }
  return undefined
}

function* loadShareFeedItemSaga(action: ILoadShareFeedItemAction) {
  try {
    yield put(loadShareFeedItemRequest(action.feedItemId))

    const result = yield makeAuthenticatedRequest(
      `/api/FeedItems/${action.feedItemId}/Share/EmailAddresses`,
    )

    if (result.success === true) {
      const contacts = List<ShareContact>(
        result.results.map((r: IShareContact) => ShareContact.fromJS(r)),
      )
      yield put(loadShareFeedItemSuccess(action.feedItemId, contacts))
    } else {
      yield put(loadShareFeedItemFailure(action.feedItemId, result.message))
      yield put(errorToast('Load Share Feed Item', result.message))
    }
  } catch (err) {
    yield put(loadShareFeedItemFailure(action.feedItemId, err.message))
    yield put(errorToast('Load Share Feed Item', err.message))
  }
}

function* saveChangeTypeSaga(action: ISaveChangeTypeAction) {
  try {
    yield put(saveChangeTypeRequest(action.feedItemId))

    const changeTypeOpts = {
      method: RequestMethod.POST,
    }
    const url = `/api/FeedItems/${action.feedItemId}/Type/${action.feedItemType}`

    const result = yield makeAuthenticatedRequest(url, changeTypeOpts)

    if (result.success === true) {
      yield put(saveChangeTypeSuccess(action.feedItemId, action.feedItemType))
      yield put(
        successToast(
          'Success',
          `Feed item changed to a ${action.feedItemType}`,
        ),
      )
    } else {
      yield put(saveChangeTypeFailure(action.feedItemId))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(saveChangeTypeFailure(action.feedItemId))
    yield put(errorToast('Oops!', err.message))
  }
}

function* saveEditCommentSaga(action: ISaveEditCommentAction) {
  try {
    if (!action.comment.editState) {
      return
    }

    yield put(saveEditCommentRequest(action.feedItemId, action.comment.id))

    const editCommentOpts = {
      body: JSON.stringify({
        id: action.comment.id,
        isPublic: action.comment.editState.isPublic,
        markup: convertToMarkup(
          action.comment.editState.markupState.getCurrentContent(),
        ),
        parentId: action.feedItemId,
      }),
      method: RequestMethod.POST,
    }
    const url = `/api/FeedItems/${action.comment.id}/Comments/Edit`

    const result = yield makeAuthenticatedRequest(url, editCommentOpts)

    if (result.success === true) {
      yield put(
        saveEditCommentSuccess(
          action.feedItemId,
          Comment.fromJS(result.entity),
        ),
      )
      yield put(successToast('Success', 'Comment updated'))
    } else {
      yield put(saveEditCommentFailure(action.feedItemId, action.comment.id))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(saveEditCommentFailure(action.feedItemId, action.comment.id))
    yield put(errorToast('Oops!', err.message))
  }
}

function* saveEditFeedItemSaga(action: ISaveEditFeedItemAction) {
  try {
    if (!action.feedItem.editState) {
      return
    }

    yield put(saveEditFeedItemRequest(action.feedItem.id))

    let attachments = yield select(selectAttachments(action.feedItem.id))
    attachments = attachments.valueSeq().map((item: any) => {
      if (!item.error) {
        return item.attachment
      }

      return false
    })

    const editFeedItemOpts = {
      body: JSON.stringify({
        attachments,
        createdOn: action.feedItem.createdOn,
        id: action.feedItem.id,
        isPublic: action.feedItem.editState.isPublic,
        leadPriority: action.feedItem.editState.leadPriority,
        leadType: action.feedItem.editState.leadType,
        markup: convertToMarkup(
          action.feedItem.editState.markupState.getCurrentContent(),
        ),
        title: action.feedItem.editState.title,
      }),
      method: RequestMethod.POST,
    }

    const result = yield makeAuthenticatedRequest(
      '/api/FeedItems/Edit',
      editFeedItemOpts,
    )

    if (result.success === true) {
      yield put(saveEditFeedItemSuccess(FeedItem.fromJS(result.entity)))
      yield put(successToast('Success', 'Feed item updated'))
      yield put(fileUploadActions.clearFileList(action.feedItem.id))
    } else {
      yield put(saveEditFeedItemFailure(action.feedItem.id))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(saveEditFeedItemFailure(action.feedItem.id))
    yield put(errorToast('Oops!', err.message))
  }
}

function* shareFeedItemSaga(action: IShareFeedItemAction) {
  try {
    yield put(shareFeedItemRequest(action.shareInfo.feedItemId))

    const contactEmails = action.contacts
      .map((item: any) => {
        return item.addresses
      })
      .reduce((pre: any, current: any) => {
        return pre.concat(current)
      })

    const shareBody = {
      body: JSON.stringify({
        comment: action.comment,
        contacts: contactEmails.toArray(),
      }),
      method: RequestMethod.POST,
    }

    const result = yield makeAuthenticatedRequest(
      `/api/FeedItems/${action.shareInfo.feedItemId}/Share`,
      shareBody,
    )

    if (result.success === true) {
      yield put(shareFeedItemSuccess(action.shareInfo.feedItemId))
      yield put(successToast('Success!', 'Feed Item has been shared'))
    } else {
      yield put(
        shareFeedItemFailure(action.shareInfo.feedItemId, result.message),
      )
      yield put(errorToast('Share Feed Item Failed', result.message))
    }
  } catch (err) {
    yield put(shareFeedItemFailure(action.shareInfo.feedItemId, err.message))
    yield put(errorToast('Share Feed Item Failed', err.message))
  }
}

function* toggleCommentFavoriteSaga(action: IToggleCommentFavoriteAction) {
  try {
    const url = `/api/FeedItems/Comments/My/${action.commentId}`

    const opts: IRequestOptions = {
      method: action.favorite ? RequestMethod.POST : RequestMethod.DELETE,
    }

    const result = yield makeAuthenticatedRequest(url, opts)

    if (result.success === true) {
      yield put(
        toggleCommentFavoriteSuccess(
          action.feedItemId,
          action.commentId,
          action.userId,
          action.favorite,
        ),
      )
    } else {
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
  }
}

function* toggleFeedItemFavoriteSaga(action: IToggleFeedItemFavoriteAction) {
  try {
    const url = `/api/FeedItems/My/${action.feedItemId}`

    const opts: IRequestOptions = {
      method: action.favorite ? RequestMethod.POST : RequestMethod.DELETE,
    }

    const result = yield makeAuthenticatedRequest(url, opts)

    if (result.success) {
      yield put(
        toggleFeedItemFavoriteSuccess(
          action.feedItemId,
          action.userId,
          action.favorite,
        ),
      )
    } else {
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchArchiveLead() {
  yield takeLatest(ActionTypes.ArchiveLead, archiveLeadSaga)
}

function* watchDeleteComment() {
  yield takeEvery(ActionTypes.DeleteComment, deleteCommentSaga)
}

function* watchDeleteFeedItem() {
  yield takeEvery(ActionTypes.DeleteFeedItem, deleteFeedItemSaga)
}

function* watchLoadComments() {
  yield takeEvery(ActionTypes.LoadComments, loadCommentsSaga)
}

function* watchLoadFeedItem() {
  yield takeLatest(ActionTypes.LoadFeedItem, loadFeedItemSaga)
}

function* watchLoadFeedItems() {
  yield takeLatest(ActionTypes.LoadFeedItems, loadFeedItemsSaga)
}

function* watchLoadShareFeedItem() {
  yield takeLatest(ActionTypes.LoadShareFeedItem, loadShareFeedItemSaga)
}

function* watchSaveChangeType() {
  yield takeLatest(ActionTypes.SaveChangeType, saveChangeTypeSaga)
}

function* watchSaveEditComment() {
  yield takeLatest(ActionTypes.SaveEditComment, saveEditCommentSaga)
}

function* watchSaveEditFeedItem() {
  yield takeLatest(ActionTypes.SaveEditFeedItem, saveEditFeedItemSaga)
}

function* watchShareFeedItem() {
  yield takeLatest(ActionTypes.ShareFeedItem, shareFeedItemSaga)
}

function* watchToggleCommentFavorite() {
  yield takeEvery(ActionTypes.ToggleCommentFavorite, toggleCommentFavoriteSaga)
}

function* watchToggleFeedItemFavorite() {
  yield takeEvery(
    ActionTypes.ToggleFeedItemFavorite,
    toggleFeedItemFavoriteSaga,
  )
}

export function* feedItemsRootSaga() {
  yield fork(watchArchiveLead)
  yield fork(watchDeleteComment)
  yield fork(watchDeleteFeedItem)
  yield fork(watchLoadComments)
  yield fork(watchLoadFeedItem)
  yield fork(watchLoadFeedItems)
  yield fork(watchLoadShareFeedItem)
  yield fork(watchSaveChangeType)
  yield fork(watchSaveEditComment)
  yield fork(watchSaveEditFeedItem)
  yield fork(watchShareFeedItem)
  yield fork(watchToggleCommentFavorite)
  yield fork(watchToggleFeedItemFavorite)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = (state: ApplicationState) => state.feedItems
const selectFileUploadState = (state: ApplicationState) => state.fileUpload

export const selectAttachments = (key: string) =>
  createSelector(selectFileUploadState, (mState) => {
    return mState.files.get(key)
  })

export const selectError = () =>
  createSelector(selectModuleState, (mState) => mState.error)

export const selectIsLoading = () =>
  createSelector(selectModuleState, (mState) => mState.isLoading)

export const selectFeedItem = (feedItemId: string) =>
  createSelector(selectModuleState, (mState) => {
    const feedItem = mState.items.get(feedItemId)
    return feedItem || new FeedItem()
  })

export const selectShareItem = (feedItemId: string) =>
  createSelector(selectModuleState, (mState): ShareFeedItem | undefined =>
    mState.shareItems.get(feedItemId),
  )
